import React from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="pt-28 md:pt-32 pb-12 bg-gradient-to-br from-primary to-secondary text-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl text-white/90">
              Informativa sulla protezione dei dati personali
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            <div className="prose prose-lg max-w-none">
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-l-4 border-primary p-6 rounded-r-lg mb-8">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Ultimo aggiornamento:</strong> Dicembre 2024
                </p>
                <p className="text-sm text-gray-600">
                  Marchio di proprietà di <strong>Novastart Srl</strong>
                </p>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">1. Titolare del Trattamento</h2>
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <p className="mb-2"><strong>Ragione Sociale:</strong> Novastart Srl</p>
                <p className="mb-2"><strong>Sede Legale:</strong> Via Privata Del Gonfalone 3, Milano (MI) 20123</p>
                <p className="mb-2"><strong>P.IVA:</strong> 03373610785</p>
                <p className="mb-2"><strong>Email:</strong> info@servizioclientienergia.com</p>
                <p className="mb-2"><strong>Telefono:</strong> 06 60513535 - 06 92963501</p>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">2. Dati Personali Raccolti</h2>
              <p className="mb-4">
                Nel corso della navigazione sul nostro sito e dell'utilizzo dei nostri servizi, 
                possiamo raccogliere le seguenti categorie di dati personali:
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Dati identificativi:</strong> nome, cognome, ragione sociale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Dati di contatto:</strong> indirizzo email, numero di telefono, indirizzo postale</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Dati di navigazione:</strong> indirizzo IP, tipo di browser, sistema operativo, pagine visitate</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Dati relativi ai consumi:</strong> informazioni sulle tue utenze e consumi energetici (se fornite volontariamente)</span>
                </li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">3. Finalità del Trattamento</h2>
              <p className="mb-4">I tuoi dati personali vengono trattati per le seguenti finalità:</p>
              <div className="space-y-4 mb-8">
                <div className="border-l-4 border-primary pl-4">
                  <h4 className="font-bold text-gray-800">a) Servizi richiesti</h4>
                  <p className="text-gray-600">Erogazione dei servizi di comparazione offerte e supporto nella scelta delle migliori tariffe energetiche.</p>
                </div>
                <div className="border-l-4 border-secondary pl-4">
                  <h4 className="font-bold text-gray-800">b) Comunicazioni commerciali</h4>
                  <p className="text-gray-600">Invio di newsletter, offerte promozionali e aggiornamenti sui nostri servizi (solo previo consenso).</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <h4 className="font-bold text-gray-800">c) Miglioramento dei servizi</h4>
                  <p className="text-gray-600">Analisi statistica e miglioramento della qualità dei nostri servizi.</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h4 className="font-bold text-gray-800">d) Obblighi di legge</h4>
                  <p className="text-gray-600">Adempimento di obblighi previsti da leggi, regolamenti e normativa comunitaria.</p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">4. Base Giuridica del Trattamento</h2>
              <p className="mb-4">Il trattamento dei dati personali si basa su:</p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span><strong>Consenso esplicito</strong> dell'interessato per finalità di marketing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span><strong>Esecuzione di un contratto</strong> o misure precontrattuali</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span><strong>Legittimo interesse</strong> del Titolare per migliorare i servizi offerti</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span><strong>Obblighi di legge</strong> previsti dalla normativa vigente</span>
                </li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">5. Conservazione dei Dati</h2>
              <p className="mb-8">
                I dati personali saranno conservati per il tempo strettamente necessario a conseguire le finalità 
                per cui sono stati raccolti. In generale, i dati vengono conservati per <strong>24 mesi</strong> dalla 
                data dell'ultimo contatto o interazione, salvo diversi obblighi di legge che richiedano una conservazione più lunga.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">6. Condivisione dei Dati</h2>
              <p className="mb-4">I tuoi dati personali possono essere comunicati a:</p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Fornitori di servizi energetici</strong> per l'attivazione delle offerte richieste</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Partner commerciali</strong> con cui collaboriamo per l'erogazione dei servizi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Fornitori di servizi IT</strong> per la gestione tecnica del sito</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Autorità pubbliche</strong> quando richiesto dalla legge</span>
                </li>
              </ul>
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
                <p className="text-green-800">
                  <strong>Importante:</strong> Non vendiamo né cediamo i tuoi dati personali a terze parti 
                  per finalità di marketing senza il tuo consenso esplicito.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">7. I Tuoi Diritti</h2>
              <p className="mb-4">In qualità di interessato, hai diritto di:</p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Accesso</h5>
                  <p className="text-sm text-gray-600">Ottenere conferma dell'esistenza dei tuoi dati</p>
                </div>
                <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Rettifica</h5>
                  <p className="text-sm text-gray-600">Correggere dati inesatti o incompleti</p>
                </div>
                <div className="bg-gradient-to-br from-accent/5 to-accent/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Cancellazione</h5>
                  <p className="text-sm text-gray-600">Richiedere la rimozione dei tuoi dati</p>
                </div>
                <div className="bg-gradient-to-br from-primary/5 to-secondary/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Opposizione</h5>
                  <p className="text-sm text-gray-600">Opporti al trattamento dei tuoi dati</p>
                </div>
                <div className="bg-gradient-to-br from-secondary/5 to-accent/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Portabilità</h5>
                  <p className="text-sm text-gray-600">Ricevere i dati in formato strutturato</p>
                </div>
                <div className="bg-gradient-to-br from-accent/5 to-primary/10 p-4 rounded-lg">
                  <h5 className="font-bold text-gray-800 mb-2">Limitazione</h5>
                  <p className="text-sm text-gray-600">Limitare il trattamento dei dati</p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">8. Sicurezza dei Dati</h2>
              <p className="mb-8">
                Adottiamo misure tecniche e organizzative adeguate per proteggere i dati personali da accessi non autorizzati, 
                divulgazione, modifica o distruzione. Utilizziamo protocolli di sicurezza SSL/TLS, sistemi di firewall e 
                procedure di backup regolari.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">9. Cookie</h2>
              <p className="mb-8">
                Il nostro sito utilizza cookie tecnici e, previo consenso, cookie di profilazione. 
                Per maggiori informazioni consulta la nostra{' '}
                <a href="/cookie-policy" className="text-primary hover:text-secondary underline font-semibold">
                  Cookie Policy
                </a>.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">10. Modifiche alla Privacy Policy</h2>
              <p className="mb-8">
                Ci riserviamo il diritto di modificare questa informativa in qualsiasi momento. 
                Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">11. Contatti</h2>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-xl border border-gray-200">
                <p className="mb-4">
                  Per esercitare i tuoi diritti o per qualsiasi informazione riguardo al trattamento dei dati personali, 
                  puoi contattarci:
                </p>
                <div className="space-y-2">
                  <p><strong>Email:</strong> info@servizioclientienergia.com</p>
                  <p><strong>Telefono:</strong> 06 60513535 - 06 92963501</p>
                  <p><strong>Indirizzo:</strong> Via Privata Del Gonfalone 3, Milano (MI) 20123</p>
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  Hai anche il diritto di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
